/**
 * The Workspace toolbar (contracts-plan.md §11 tab 4): Apply rate card,
 * Apply previous study, Accept all (with the proposed count), Export, and a
 * quiet Reload. CHROMELESS — not wrapped in a card (design skill v2: "Filter
 * bar — CHROMELESS ... toolbars are page chrome, not content").
 *
 * Export stays enabled under the R32 read-only rule ("Reading, selection,
 * hover, copy and Export stay available") — every other write action is
 * disabled with a `title` naming which of the four reasons applies.
 */
import { FileSpreadsheet, History, CheckCheck, Download, RotateCw } from 'lucide-react';
import { secondaryBtnCls, outlineBtnCls } from '../../../ui';
import type { ExportPathMode } from '../../api/workspace';

interface Props {
  readOnly: boolean;
  readOnlyReason: string | null;
  proposedCount: number;
  accepting: boolean;
  excelCheckPassed: Record<ExportPathMode, boolean>;
  onApplyRateCard: () => void;
  onApplyPreviousStudy: () => void;
  onAcceptAll: () => void;
  onExport: () => void;
  onReload: () => void;
}

export function Toolbar({
  readOnly,
  readOnlyReason,
  proposedCount,
  accepting,
  excelCheckPassed,
  onApplyRateCard,
  onApplyPreviousStudy,
  onAcceptAll,
  onExport,
  onReload,
}: Props) {
  const bothVerified = excelCheckPassed.edit_in_place && excelCheckPassed.own_version;
  const writeTitle = readOnly ? readOnlyReason ?? 'Read-only' : undefined;

  return (
    <div data-testid="ws-toolbar" className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        data-testid="ws-apply-rate-card"
        className={secondaryBtnCls}
        disabled={readOnly}
        title={writeTitle}
        onClick={onApplyRateCard}
      >
        <FileSpreadsheet size={16} /> Apply rate card
      </button>
      <button
        type="button"
        data-testid="ws-apply-previous-study"
        className={secondaryBtnCls}
        disabled={readOnly}
        title={writeTitle}
        onClick={onApplyPreviousStudy}
      >
        <History size={16} /> Apply previous study
      </button>
      <button
        type="button"
        data-testid="ws-accept-all"
        className={secondaryBtnCls}
        disabled={readOnly || proposedCount === 0 || accepting}
        title={readOnly ? writeTitle : proposedCount === 0 ? 'Nothing proposed' : undefined}
        onClick={onAcceptAll}
      >
        <CheckCheck size={16} /> Accept all{proposedCount > 0 ? ` (${proposedCount})` : ''}
      </button>
      <button type="button" data-testid="ws-export" className={secondaryBtnCls} onClick={onExport}>
        <Download size={16} /> Export
        {!bothVerified && (
          <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            unverified in Excel
          </span>
        )}
      </button>
      <button type="button" data-testid="ws-reload" className={outlineBtnCls()} title="Reload from the server" onClick={onReload}>
        <RotateCw size={16} />
      </button>
    </div>
  );
}

export default Toolbar;
